import React, { useEffect, useState } from "react";
import axios from "../../api/axios";

// MUI components
import {
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Typography,
  MenuItem,
} from "@mui/material";
import Icon from "@mui/material/Icon";

// Custom components
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

function Deductions() {
  const [deductions, setDeductions] = useState([]);
  const [structures, setStructures] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [steps, setSteps] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    id: null,
    name: "",
    salary_structures_id: "",
    grade_level_id: "",
    step_id: "",
    type: "",
    value: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchDeductions();
    fetchStructures();
    fetchGradeLevels();
    fetchSteps();
  }, []);

  const fetchDeductions = async () => {
    try {
      const res = await axios.get("/payroll/deductions");
      setDeductions(res.data);
    } catch (err) {
      console.error("Error fetching deductions", err);
    }
  };

  const fetchStructures = async () => {
    const res = await axios.get("/salary-structures");
    setStructures(res.data);
  };

  const fetchGradeLevels = async () => {
    const res = await axios.get("/grade-levels");
    setGradeLevels(res.data);
  };

  const fetchSteps = async () => {
    const res = await axios.get("/steps");
    setSteps(res.data);
  };

  const handleSave = async () => {
    const payload = {
      name: currentItem.name,
      salary_structures_id: currentItem.salary_structures_id,
      grade_level_id: currentItem.grade_level_id,
      step_id: currentItem.step_id,
      type: currentItem.type,
      value: parseFloat(currentItem.value),
    };

    try {
      if (currentItem.id) {
        await axios.put(`/payroll/deductions/${currentItem.id}`, payload);
        setSnackbar({ open: true, message: "Deduction updated successfully" });
      } else {
        await axios.post(`/payroll/deductions`, payload);
        setSnackbar({ open: true, message: "Deduction created successfully" });
      }

      fetchDeductions();
      setOpenDialog(false);
      resetForm();
    } catch (err) {
      console.error("Save failed", err);
      alert("Save failed. Please check required fields.");
    }
  };

  const resetForm = () => {
    setCurrentItem({
      id: null,
      name: "",
      salary_structures_id: "",
      grade_level_id: "",
      step_id: "",
      type: "",
      value: "",
    });
  };

  const handleEdit = (item) => {
    setCurrentItem({
      id: item.id,
      name: item.name,
      salary_structures_id: item.salary_structures_id || "",
      grade_level_id: item.grade_level_id || "",
      step_id: item.step_id || "",
      type: item.type || "",
      value: item.value || "",
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this deduction?")) {
      try {
        await axios.delete(`/payroll/deductions/${id}`);
        setSnackbar({ open: true, message: "Deleted successfully" });
        fetchDeductions();
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  const paginatedItems = deductions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container justifyContent="space-between" alignItems="center" px={2} mb={2}>
          <Typography variant="h5">Deductions</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenDialog(true)}
            sx={{ color: "#fff" }}
          >
            Add New
          </Button>
        </Grid>

        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
          <Table>
            {/* <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f2f5" }}>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Structure</TableCell>
                <TableCell>Grade Level</TableCell>
                <TableCell>Step</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead> */}
            <TableBody>
              {paginatedItems.length > 0 ? (
                paginatedItems.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      {structures.find((s) => s.id === item.salary_structures_id)?.name || "N/A"}
                    </TableCell>
                    <TableCell>
                      {gradeLevels.find((g) => g.id === item.grade_level_id)?.name || "N/A"}
                    </TableCell>
                    <TableCell>{steps.find((s) => s.id === item.step_id)?.name || "N/A"}</TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>{item.value}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleEdit(item)}>
                        <Icon>edit</Icon>
                      </IconButton>
                      <IconButton onClick={() => handleDelete(item.id)} color="error">
                        <Icon>delete</Icon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No deductions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={deductions.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
          />
        </TableContainer>
      </MDBox>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth>
        <DialogTitle>{currentItem.id ? "Edit Deduction" : "Add Deduction"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Deduction Name"
            fullWidth
            margin="normal"
            value={currentItem.name}
            onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
          />
          <TextField
            select
            label="Salary Structure"
            fullWidth
            margin="normal"
            value={currentItem.salary_structures_id}
            onChange={(e) =>
              setCurrentItem({ ...currentItem, salary_structures_id: e.target.value })
            }
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {structures.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Grade Level"
            fullWidth
            margin="normal"
            value={currentItem.grade_level_id}
            onChange={(e) => setCurrentItem({ ...currentItem, grade_level_id: e.target.value })}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {gradeLevels.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Step"
            fullWidth
            margin="normal"
            value={currentItem.step_id}
            onChange={(e) => setCurrentItem({ ...currentItem, step_id: e.target.value })}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {steps.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Type"
            fullWidth
            margin="normal"
            value={currentItem.type}
            onChange={(e) => setCurrentItem({ ...currentItem, type: e.target.value })}
          >
            <MenuItem value="fixed">Fixed</MenuItem>
            <MenuItem value="percentage">Percentage</MenuItem>
          </TextField>

          <TextField
            label="Value"
            fullWidth
            margin="normal"
            type="number"
            value={currentItem.value}
            onChange={(e) => setCurrentItem({ ...currentItem, value: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary" sx={{ color: "#fff" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        message={snackbar.message}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Deductions;
